import React, { useState } from "react";
import AsyncSelect from "react-select/async";
import "./CustomReports.css";
import {
  adPositions,
  BrandsTypes,
  customReportDimensions,
  customReportMetrics,
  teams,
} from "../../../utils/constants";
import closeSvg from "../../../assets/images/close-circle-red.svg";
import rightArrowImg from "../../../assets/images/arrow_back_icon.svg";
import Datepicker from "react-tailwindcss-datepicker";
import API from "../../../services/api";
import { Toast } from "../../../shared/Toast";
import { debouncePromise } from "../../../utils/Debounce";
import CustomReportGeneration from "./CustomReportGeneration";

function CustomReportCreation(props) {
  let { editData } = props;
  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7); // Subtract 7 days

  const formatFullDate = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const formattedStartDate = formatFullDate(sevenDaysAgo);
  const formattedEndDate = formatFullDate(today);
  const [reportName, setReportName] = useState(editData?.report_name || "");
  const [dateRange, setDateRange] = useState(
    editData?.start_date
      ? {
          startDate: formatFullDate(new Date(editData.start_date * 1000)),
          endDate: formatFullDate(new Date(editData.end_date * 1000)),
        }
      : { startDate: formattedStartDate, endDate: formattedEndDate }
  );
  const [filters, setFilters] = useState("");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState(
    editData?.selected_filters || {}
  );
  const [showReportGeneration, setShowReportGeneration] = useState(false);

  // Filter options data
  const [filterOptions, setFilterOptions] = useState({
    new_user_type: {
      label: "Sales Type",
      options: [{ label: "Main", value: -1 }, ...BrandsTypes],
      async: false,
    },
    position: {
      label: "Position",
      options: adPositions.map((i) => ({ label: i, value: i })),
      async: false,
    },
    team: {
      label: "Team",
      options: teams,
      async: false,
    },
    brand_id: {
      label: "Brand",
      options: editData?.brand_options || [],
      async: true,
    },
    order_id: {
      label: "Order",
      options: editData?.order_options || [],
      async: true,
    },
  });

  const [dimensions, setDimensions] = useState(editData?.dimensions || []);

  const [metrics, setMetrics] = useState(editData?.metrics || []);

  const handleSubmit = async () => {
    if (
      !reportName ||
      !dateRange.startDate ||
      !dateRange.endDate ||
      !dimensions.length ||
      !metrics.length
    ) {
      Toast("error", "Please fill all the fields");
      return;
    }

    try {
      let result = null;
      if (editData?.cust_rep_id) {
        result = await API({
          url: "/custom_reports/edit-report",
          method: "POST",
          responseType: "json",
          data: {
            cust_rep_id: editData.cust_rep_id,
            report_name: reportName,
            start_date: dateRange.startDate,
            end_date: dateRange.endDate,
            selected_filters: selectedFilters,
            dimensions,
            metrics,
          },
        });
      } else {
        result = await API({
          url: "/custom_reports/add-report",
          method: "POST",
          responseType: "json",
          data: {
            report_name: reportName,
            start_date: dateRange.startDate,
            end_date: dateRange.endDate,
            selected_filters: selectedFilters,
            dimensions,
            metrics,
          },
        });
      }
      if (result.code === 200) {
        Toast("success", "Added successfully");
        props.onBack(true);
      } else {
        Toast("error", result.data.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Error @ custom report adding and edit", error);
    }
  };

  const handleDateRange = (selectedDate) => {
    if (selectedDate) {
      if (selectedDate.startDate && selectedDate.endDate) {
        setDateRange(selectedDate);
      } else {
        setDateRange({ startDate: null, endDate: null });
      }
    } else {
      setDateRange({ startDate: null, endDate: null });
    }
  };

  const getBrands = async (inputStr) => {
    try {
      if (!inputStr) return [];
      let requestObj = null;
      if (typeof inputStr === "number") {
        requestObj = { brand_id: +inputStr };
      } else if (inputStr) {
        requestObj = { search: inputStr };
      }
      if (!requestObj) return [];
      const brandsResult = await API({
        url: "user/sales_persons/getBrands",
        method: "POST",
        responseType: "json",
        data: requestObj,
      });
      if (brandsResult?.code === 200) {
        setFilterOptions((prev) => ({
          ...prev,
          brand_id: {
            label: "Brand",
            options: brandsResult.data,
            async: true,
          },
        }));
        return brandsResult.data;
      } else return [];
    } catch (error) {
      console.error("Error @ getBrands", error);
    }
  };

  const getAsyncOrderIds = async (inputStr) => {
    try {
      if (!inputStr) return [];
      let requestObj = { input_string: inputStr };
      const orderIdsResult = await API({
        url: "order/getOrderIdsByString",
        method: "POST",
        responseType: "json",
        data: requestObj,
      });
      if (orderIdsResult?.code === 200) {
        setFilterOptions((prev) => ({
          ...prev,
          order_id: {
            label: "Order",
            options: orderIdsResult.data,
            async: true,
          },
        }));
        return orderIdsResult.data;
      } else return [];
    } catch (error) {
      console.error("Error @ getAsyncOrderIds", error);
    }
  };

  const debouncedGetFilterOptions = (type) => {
    switch (type) {
      case "brand_id":
        return debouncePromise(getBrands, 500);
      case "order_id":
        return debouncePromise(getAsyncOrderIds, 500);
      default:
        return [];
    }
  };

  const defaultValue = (options, value) => {
    return options ? options.find((option) => option.value === value) : "";
  };

  const handleGenerateReport = () => {
    try {
      setShowReportGeneration(true);
    } catch (error) {
      console.error("Error @ handleGenerateReport", error);
    }
  };
  if (showReportGeneration) {
    return (
      <CustomReportGeneration
        onBack={() => setShowReportGeneration(false)}
        APIbody={{
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
          selected_filters: selectedFilters,
          dimensions: dimensions,
          metrics: metrics,
        }}
        report_name={editData?.report_name || ""}
      />
    );
  }

  return (
    <div className="custom-reports-container">
      <div className="reports-content">
        <header className="reports-header">
          <button onClick={() => props.onBack()} className="" type="button">
            All Reports
          </button>
          <img
            src={rightArrowImg}
            alt="right arrow"
            className="right-arrow-icon"
          />
          <h1>{editData?.cust_rep_id ? editData.report_name : "New Report"}</h1>
        </header>

        <div className="general-settings">
          <h2>General Setting</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="reportName">Report Name</label>
              <input
                type="text"
                id="reportName"
                placeholder="Report Name"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="dateRange">Date Range</label>
              <div className="date-input-wrapper">
                <i className="fas fa-calendar-alt"></i>
                <div className="border rounded-lg  border-gray-300 hover:cursor-pointer hover:border-2 hover:border-sky-500">
                  <Datepicker
                    className="form-input date-input border rounded-lg"
                    value={dateRange}
                    primaryColor={"cyan"}
                    showShortcuts={true}
                    useRange={true} // Allow date range selection
                    configs={{
                      shortcuts: {
                        today: "today",
                        yesterday: "yesterday",
                        currentMonth: "currentMonth",
                        pastMonth: "lastMonth",
                      },
                    }}
                    onChange={handleDateRange}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="filters-section">
            <h2 htmlFor="filters">Filters</h2>
            <div className="selected-filters">
              {console.log(selectedFilters)}
              {Object.keys(selectedFilters)
                .filter((filterKey) => selectedFilters[filterKey]?.length > 0)
                .map((filterKey, index) => (
                  <div key={index} className="filter-tag">
                    <div>
                      <span className="font-bold">
                        {filterOptions[filterKey].label}
                      </span>
                      <ul>
                        {selectedFilters[filterKey].map((item) => (
                          <li className="d-flex items-center justify-between">
                            <span>
                              {filterOptions[filterKey]?.options?.find(
                                (option) => option.value === item
                              )?.label || ""}
                            </span>
                            <button
                              onClick={() =>
                                setSelectedFilters((prev) => ({
                                  ...prev,
                                  [filterKey]: prev[filterKey].filter(
                                    (val) => val !== item
                                  ),
                                }))
                              }
                              className="remove-filter"
                              type="button"
                            >
                              <img src={closeSvg} alt="close" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
            </div>
            <div className="filters-input-wrapper">
              <i className="fas fa-filter"></i>
              <input
                type="text"
                id="filters"
                placeholder="Sales Type, Position, Team,..."
                value={filters}
                onChange={(e) => setFilters(e.target.value)}
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className="form-input filters-input"
                readOnly
              />

              {/* Filter Dropdown */}
              {showFilterDropdown && (
                <div
                  className="filter-dropdown-mask"
                  onClick={() => setShowFilterDropdown(false)}
                >
                  <div
                    className="filter-dropdown"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="filter-dropdown-content">
                      {Object.keys(filterOptions).map((filterKey) => (
                        <div key={filterKey} className="filter-group">
                          <h4 className="filter-group-title">
                            {filterOptions[filterKey].label}
                          </h4>
                          <div className="filter-options-grid">
                            {filterOptions[filterKey].async ? (
                              <AsyncSelect
                                isMulti
                                className=""
                                name={filterKey}
                                placeholder={`Select ${filterOptions[filterKey].label}`}
                                value={
                                  selectedFilters[filterKey]?.length > 0
                                    ? selectedFilters[filterKey].map((val) =>
                                        defaultValue(
                                          filterOptions[filterKey].options,
                                          val
                                        )
                                      )
                                    : []
                                }
                                onChange={(selected) => {
                                  const selectedValues = selected
                                    ? selected.map((item) => item.value)
                                    : [];
                                  setSelectedFilters((prev) => ({
                                    ...prev,
                                    [filterKey]: selectedValues,
                                  }));
                                }}
                                loadOptions={debouncedGetFilterOptions(
                                  filterKey
                                )}
                                defaultOptions={
                                  filterOptions[filterKey].options
                                }
                                noOptionsMessage={() =>
                                  "Type searching " +
                                  filterOptions[filterKey].label
                                }
                                cacheOptions
                              />
                            ) : (
                              filterOptions[filterKey].options.map(
                                (option, index) => (
                                  <label
                                    key={`${filterKey}-${index}`}
                                    className="filter-checkbox-item"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedFilters[
                                        filterKey
                                      ]?.includes(option.value)}
                                      onChange={() =>
                                        setSelectedFilters((prev) => {
                                          const currentValues =
                                            prev[filterKey] || [];
                                          const isChecked =
                                            currentValues.includes(
                                              option.value
                                            );

                                          if (isChecked) {
                                            // Unchecking - remove the option
                                            return {
                                              ...prev,
                                              [filterKey]: currentValues.filter(
                                                (val) => val !== option.value
                                              ),
                                            };
                                          } else {
                                            // Checking - add the option
                                            return {
                                              ...prev,
                                              [filterKey]: [
                                                ...currentValues,
                                                option.value,
                                              ],
                                            };
                                          }
                                        })
                                      }
                                    />
                                    <span className="filter-checkmark"></span>
                                    {option.label}
                                  </label>
                                )
                              )
                            )}
                          </div>
                        </div>
                      ))}

                      <div className="filter-dropdown-actions">
                        <button
                          type="button"
                          onClick={() => setShowFilterDropdown(false)}
                          className="filter-cancel-btn"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowFilterDropdown(false)}
                          className="filter-apply-btn"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="settings-grid">
          <div className="dimensions-section">
            {console.log(dimensions)}
            <h3>Dimensions</h3>
            <div className="checkbox-list">
              {customReportDimensions.map((item) => (
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={dimensions.includes(item.value)}
                    onChange={() => {
                      if (dimensions.includes(item.value))
                        setDimensions((prev) => [
                          ...prev.filter((i) => i !== item.value),
                        ]);
                      else setDimensions((prev) => [...prev, item.value]);
                    }}
                  />
                  <span className="checkmark"></span>
                  {item.label}
                </label>
              ))}
            </div>
          </div>

          <div className="metrics-section">
            <h3>Metrics</h3>
            <div className="checkbox-list">
              {customReportMetrics.map((item) => (
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={metrics.includes(item.value)}
                    onChange={() => {
                      if (metrics.includes(item.value))
                        setMetrics((prev) => [
                          ...prev.filter((i) => i !== item.value),
                        ]);
                      else setMetrics((prev) => [...prev, item.value]);
                    }}
                  />
                  <span className="checkmark"></span>
                  {item.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="submit-section">
          <button
            onClick={handleGenerateReport}
            className="report-btn"
            type="button"
          >
            Generate Report
          </button>
          <button onClick={handleSubmit} className="submit-btn" type="button">
            {`${editData?.cust_rep_id ? "Update" : "Create"}`} Report
          </button>
        </div>
      </div>
    </div>
  );
}

export default CustomReportCreation;
